import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MapPin } from 'lucide-react';
import type { Address } from '@/types/firestore';

// US States
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'Washington D.C.' },
];

// Canadian Provinces
const CA_PROVINCES = [
  { code: 'AB', name: 'Alberta' },
  { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' },
  { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' },
  { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' },
  { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' },
  { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' },
  { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
];

interface AddressFormProps {
  value: Partial<Address>;
  onChange: (address: Partial<Address>) => void;
  errors?: Partial<Record<keyof Address, string>>;
  required?: boolean;
  /** Fixed country - hides the country toggle when set */
  fixedCountry?: 'US' | 'CA';
}

export function AddressForm({ value, onChange, errors, required = true, fixedCountry }: AddressFormProps) {
  const country = fixedCountry || value.country || 'US';
  const regions = country === 'US' ? US_STATES : CA_PROVINCES;
  const zipLabel = country === 'US' ? 'ZIP Code' : 'Postal Code';
  const zipPlaceholder = country === 'US' ? '12345' : 'A1A 1A1';
  const stateLabel = country === 'US' ? 'State' : 'Province';

  const handleChange = (field: keyof Address, fieldValue: string) => {
    // Always include the fixed country if set
    const updates = { ...value, [field]: fieldValue };
    if (fixedCountry) {
      updates.country = fixedCountry;
    }
    onChange(updates);
  };

  // Format Canadian postal code
  const formatPostalCode = (input: string, isCanadian: boolean): string => {
    if (!isCanadian) return input;
    // Remove spaces and convert to uppercase
    const clean = input.replace(/\s/g, '').toUpperCase();
    // Format as A1A 1A1
    if (clean.length <= 3) return clean;
    return `${clean.slice(0, 3)} ${clean.slice(3, 6)}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm font-medium">
        <MapPin className="h-4 w-4 text-muted-foreground" />
        <span>Project Address</span>
        {!required && <span className="text-muted-foreground">(Optional)</span>}
      </div>

      {/* Street Address */}
      <div className="space-y-2">
        <Label htmlFor="street">Street Address</Label>
        <Input
          id="street"
          value={value.street || ''}
          onChange={(e) => handleChange('street', e.target.value)}
          placeholder="123 Main Street"
          className={errors?.street ? 'border-destructive' : ''}
        />
        {errors?.street && <p className="text-xs text-destructive" role="alert">{errors.street}</p>}
      </div>

      {/* City and State/Province */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label htmlFor="city">City</Label>
          <Input
            id="city"
            value={value.city || ''}
            onChange={(e) => handleChange('city', e.target.value)}
            placeholder="City"
            className={errors?.city ? 'border-destructive' : ''}
          />
          {errors?.city && <p className="text-xs text-destructive" role="alert">{errors.city}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="state">{stateLabel}</Label>
          <Select
            value={value.state || ''}
            onValueChange={(val) => handleChange('state', val)}
          >
            <SelectTrigger className={errors?.state ? 'border-destructive' : ''}>
              <SelectValue placeholder={`Select ${stateLabel}`} />
            </SelectTrigger>
            <SelectContent className="max-h-[200px]">
              {regions.map((region) => (
                <SelectItem key={region.code} value={region.code}>
                  {region.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors?.state && <p className="text-xs text-destructive" role="alert">{errors.state}</p>}
        </div>
      </div>

      {/* ZIP/Postal Code */}
      <div className="space-y-2">
        <Label htmlFor="zip">{zipLabel}</Label>
        <Input
          id="zip"
          value={value.zip || ''}
          onChange={(e) => {
            const formatted = formatPostalCode(e.target.value, country === 'CA');
            handleChange('zip', formatted);
          }}
          placeholder={zipPlaceholder}
          maxLength={country === 'US' ? 10 : 7}
          className={`w-32 ${errors?.zip ? 'border-destructive' : ''}`}
        />
        {errors?.zip && <p className="text-xs text-destructive" role="alert">{errors.zip}</p>}
      </div>
    </div>
  );
}
